import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { FEATURE_FLAG_LABELS, FeatureFlag } from '../../models/tenant';
import { BillingModule, MODULE_DESCRIPTIONS, MODULE_LIMIT_UNITS, Plan } from '../../models/subscription';
import { SubscriptionService } from '../../../services/subscription.service';

/**
 * The price list. A plan or module with no price is not offered to anybody, so nothing is on sale
 * until it is entered here. Editing never touches what a lab already bought: each subscription
 * keeps the price and modules it was sold with.
 */
@Component({
  selector: 'app-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss']
})
export class PlansComponent implements OnInit {

  readonly descriptions = MODULE_DESCRIPTIONS;
  readonly units = MODULE_LIMIT_UNITS;

  plans: Plan[] = [];
  modules: BillingModule[] = [];
  loading = true;
  error: string | null = null;
  saving: string | null = null;

  constructor(private subscriptionService: SubscriptionService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    forkJoin([this.subscriptionService.getPlans(), this.subscriptionService.getModules()]).subscribe({
      next: ([plans, modules]) => {
        this.plans = plans.items;
        this.modules = modules.items;
        this.loading = false;
      },
      error: () => {
        this.error = 'Could not load the price list.';
        this.loading = false;
      }
    });
  }

  labelOf(module: BillingModule): string {
    return module.featureFlag ? FEATURE_FLAG_LABELS[module.featureFlag as FeatureFlag] : 'Core (base of a flexible plan)';
  }

  /** The modules a plan can include: everything on the list but the flexible plan's base. */
  get sellable(): BillingModule[] {
    return this.modules.filter(each => !!each.featureFlag);
  }

  includes(plan: Plan, flag: string | null): boolean {
    return plan.modules.some(each => each.featureFlag === flag);
  }

  limitOf(plan: Plan, flag: string | null): number | null {
    return plan.modules.find(each => each.featureFlag === flag)?.monthlyLimit ?? null;
  }

  toggle(plan: Plan, module: BillingModule, on: boolean): void {
    plan.modules = on
      ? [...plan.modules, { featureFlag: module.featureFlag as string, monthlyLimit: module.defaultLimit }]
      : plan.modules.filter(each => each.featureFlag !== module.featureFlag);
  }

  setLimit(plan: Plan, flag: string | null, value: number | null): void {
    const row = plan.modules.find(each => each.featureFlag === flag);
    if (row) {
      row.monthlyLimit = value;
    }
  }

  savePlan(plan: Plan): void {
    this.saving = plan.code;
    this.subscriptionService.updatePlan(plan).subscribe({
      next: saved => {
        Object.assign(plan, saved);
        this.saved(plan.name);
      },
      error: err => this.failed(err)
    });
  }

  saveModule(module: BillingModule): void {
    this.saving = module.code;
    this.subscriptionService.updateModule(module).subscribe({
      next: saved => {
        Object.assign(module, saved);
        this.saved(module.name);
      },
      error: err => this.failed(err)
    });
  }

  private saved(name: string): void {
    this.saving = null;
    this.snackBar.open(`${name} saved`, 'Close', { duration: 3000 });
  }

  private failed(err: any): void {
    this.saving = null;
    this.snackBar.open(err?.error?.exceptionMessage || 'Could not save', 'Close', { duration: 5000 });
  }
}
